import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';
import CustomAlert from '../../Common/CustomAlert';
import CustomConfirmation from '../../Common/CustomConfirmation';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface StudentDetails {
    reportNo: string;
    studyYear: string;
    mdCurriculum: string;
    hasLP: boolean | string;
    found: boolean;
    message?: string;
    schoolCode?: string;
    familyCode?: string;
    login?: string;
    country?: string;
    internalSchoolCode?: string;
}

interface LessonPlanItem {
    LessonPosition: number;
    DicName: string;
    LessonName: string;
    ActivityType: string;
    Strand: string;
    SubStrand: string;
    Year: string;
    HowAdded: string;
    LessonTime: string;
    WeekNumber: number;
    PlanCreatedOn: string;
    WeekendDate: string;
}

interface CreateMathsDoctorLessonPlanProps {
    /**
     * Replaces the VB's Session("SES_ADMIN_LOGIN") / Session("SES_SupportLogin") lookup used to
     * stamp EL_LoginStudent.MathsLPCreatedBy. There is no server session identity in this
     * stateless API, so pass the currently logged-in admin/support username in from whatever
     * holds auth state in this app (context, redux, etc). Defaults to '' (matches VB's fallback
     * when neither session value was set).
     */
    adminLogin?: string;
}

const CreateMathsDoctorLessonPlan: React.FC<CreateMathsDoctorLessonPlanProps> = ({ adminLogin = '' }) => {
    const [queryParams, setQueryParams] = useState({
        schoolCode: '',
        familyCode: '',
        login: '',
        country: '',
        studyYear: ''
    });

    const [details, setDetails] = useState<StudentDetails | null>(null);
    const [lessonPlan, setLessonPlan] = useState<LessonPlanItem[]>([]);
    const [loading, setLoading] = useState(false);

    const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });
    const [confirmInfo, setConfirmInfo] = useState<{ show: boolean; message: string; onConfirm: (() => void) | null }>({
        show: false,
        message: '',
        onConfirm: null
    });

    const getParam = (name: string): string => {
        const url = window.location.href;
        name = name.replace(/[\[\]]/g, '\\$&');
        const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
        const results = regex.exec(url);
        if (!results) return '';
        if (!results[2]) return '';
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    };

    useEffect(() => {
        const qp = {
            schoolCode: getParam('C') || getParam('SchoolCode'),
            familyCode: getParam('F') || getParam('FamilyCode'),
            login: getParam('L') || getParam('Login'),
            studyYear: getParam('GrdYr') || getParam('StudyYear'),
            country: getParam('KT') || getParam('Country')
        };

        setQueryParams(qp);

        if (qp.schoolCode && qp.familyCode && qp.login) {
            fetchStudentDetails(qp.schoolCode, qp.familyCode, qp.login);
        } else {
            // Matches VB: falls through to DisplayLP = "none" when "C" isn't present at all;
            // here we still tell the user what's missing since this is a standalone page, not
            // a server-rendered postback that just silently hides the table.
            setAlertInfo({
                show: true,
                message: `Missing required parameters. SchoolCode: ${qp.schoolCode ? '✓' : '✗'}, FamilyCode: ${qp.familyCode ? '✓' : '✗'}, Login: ${qp.login ? '✓' : '✗'}`
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchStudentDetails = async (s: string, f: string, l: string) => {
        setLoading(true);
        try {
            const url = `${API_BASE_URL}api/md-lesson-plan/details/?schoolCode=${encodeURIComponent(s)}&familyCode=${encodeURIComponent(f)}&login=${encodeURIComponent(l)}`;
            const res = await fetch(url);

            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }

            const data = await res.json();

            if (data && data.found) {
                const detailsToSet = {
                    ...data,
                    hasLP: data.hasLP !== undefined ? data.hasLP : false
                };
                setDetails(detailsToSet);

                if (data.internalSchoolCode) {
                    setQueryParams(prev => ({ ...prev, schoolCode: data.internalSchoolCode }));
                }
            } else {
                // Matches VB: gfn_ShowMessage('<%=NoRecords%>', 'A', null, fn_Ok, 'S')
                setAlertInfo({ show: true, message: data?.message || 'No records found.' });
            }
        } catch (error) {
            setAlertInfo({ show: true, message: 'Error fetching student details.' });
        } finally {
            setLoading(false);
        }
    };

    const closeConfirmation = () => {
        setConfirmInfo({ show: false, message: '', onConfirm: null });
    };

    const executeCreate = async () => {
        if (!details) return;

        setLoading(true);
        try {
            const payload = {
                schoolCode: details.internalSchoolCode || queryParams.schoolCode,
                familyCode: details.familyCode || queryParams.familyCode,
                login: details.login || queryParams.login,
                reportNo: details.reportNo,
                studyYear: details.studyYear || queryParams.studyYear,
                createdBy: adminLogin
            };

            const res = await fetch(`${API_BASE_URL}api/md-lesson-plan/create/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }

            const result = await res.json();

            if (result.success) {
                // Matches VB: LsnPlanCreated = "LessonPlan Created Sucessfully." -> gfn_ShowMessage
                setAlertInfo({ show: true, message: result.message });

                if (window.opener && !window.opener.closed) {
                    window.opener.postMessage({
                        type: 'REFRESH_PARENT',
                        source: 'CreateMathsDoctorLessonPlan',
                        studentId: queryParams.login
                    }, '*');
                }

                await fetchStudentDetails(
                    details.internalSchoolCode || queryParams.schoolCode,
                    details.familyCode || queryParams.familyCode,
                    details.login || queryParams.login
                );

                await handleView();
                setDetails(prev => prev ? { ...prev, hasLP: true } : prev);
            } else {
                // Matches VB: LsnPlanCreated = Message (the failure/error string) -> gfn_ShowMessage
                setAlertInfo({ show: true, message: result.message || 'Failed to create plan.' });
            }
        } catch (error) {
            setAlertInfo({ show: true, message: 'An error occurred during creation.' });
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!details) return;

        // Matches VB fn_Validate('Create'): if HasLP == 'Y', confirm overwrite first
        const hasLessonPlan = details.hasLP === true || details.hasLP === 'Y' || details.hasLP === 'y';
        if (hasLessonPlan) {
            setConfirmInfo({
                show: true,
                message: 'Mathematics Lesson plan already exists.<br/>Do you want to overwrite the existing lesson plan?',
                onConfirm: async () => {
                    closeConfirmation();
                    await executeCreate();
                }
            });
            return;
        }

        await executeCreate();
    };

    const handleView = async () => {
        setLoading(true);
        try {
            const schoolCode = details?.internalSchoolCode || queryParams.schoolCode;
            const familyCode = details?.familyCode || queryParams.familyCode;
            const login = details?.login || queryParams.login;

            if (!schoolCode || !familyCode || !login) {
                setAlertInfo({ show: true, message: 'Missing required parameters for view.' });
                return;
            }

            const url = `${API_BASE_URL}api/md-lesson-plan/list/?schoolCode=${encodeURIComponent(schoolCode)}&familyCode=${encodeURIComponent(familyCode)}&login=${encodeURIComponent(login)}`;
            const res = await fetch(url);

            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }

            const result = await res.json();
            // Matches VB ViewLessonPlan(): "If dtLessons.Rows.Count <= 0 Then Exit Sub" - i.e. an
            // empty result just means no table renders, not an error.
            setLessonPlan(result.data || []);
        } catch (error) {
            setAlertInfo({ show: true, message: 'Error fetching lesson plan list.' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Matches VB fn_Close(): window.close(); if (window.parent) window.parent.gfn_HidePopup();
        if (window.opener && !window.opener.closed) {
            window.opener.postMessage({ type: 'HIDE_POPUP', source: 'CreateMathsDoctorLessonPlan' }, '*');
        }
        window.close();
    };

    return (
        <div style={{
            padding: '5px',
            fontFamily: 'Arial, sans-serif',
            backgroundColor: 'white',
            minHeight: '100%',
            width: '100%',
            boxSizing: 'border-box',
            position: 'absolute',
            top: 0,
            left: 0
        }}>
            {loading && <Loading />}

            {alertInfo.show && (
                <CustomAlert
                    message={alertInfo.message}
                    onClose={() => setAlertInfo({ ...alertInfo, show: false })}
                />
            )}

            {confirmInfo.show && (
                <CustomConfirmation
                    message={confirmInfo.message}
                    onConfirm={() => confirmInfo.onConfirm?.()}
                    onClose={closeConfirmation}
                />
            )}

            <div style={{ border: 'solid 2px black', padding: '5px', backgroundColor: 'white' }}>
                <div style={{ backgroundColor: 'maroon', color: 'white', padding: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 'bold' }}>Creating Mathematics Doctor Lesson Plan</span>
                    <button
                        onClick={handleClose}
                        style={{ backgroundColor: 'orange', border: '2px solid #119ECC', color: 'white', fontWeight: 'bold', cursor: 'pointer', padding: '5px 20px' }}
                    >
                        Close
                    </button>
                </div>

                <table style={{ width: '100%', marginTop: '10px' }}>
                    <tbody>
                        <tr>
                            <td style={{ color: '#0000FF', fontWeight: 'bold' }}>
                                <span style={{ color: '#000000', fontWeight: 'normal' }}>SchoolCode: </span>
                                {
                                    queryParams.schoolCode.startsWith('ELW_')
                                        ? queryParams.schoolCode.substring(4)
                                        : queryParams.schoolCode
                                } &nbsp;&nbsp;
                                <span style={{ color: '#000000', fontWeight: 'normal' }}>FamilyCode: </span> {queryParams.familyCode} &nbsp;&nbsp;
                                <span style={{ color: '#000000', fontWeight: 'normal' }}>Login: </span> {queryParams.login} &nbsp;&nbsp;
                                <span style={{ color: '#000000', fontWeight: 'normal' }}>Country: </span> {queryParams.country} &nbsp;&nbsp;
                                <span style={{ color: '#000000', fontWeight: 'normal' }}>StudyYear: </span> {details?.studyYear || queryParams.studyYear} &nbsp;&nbsp;
                                <span style={{ color: '#000000', fontWeight: 'normal' }}>Report Number: </span> {details?.reportNo || '---'} &nbsp;&nbsp;
                                <span style={{ color: '#000000', fontWeight: 'normal' }}>MDCurriculum: </span> {details?.mdCurriculum || '---'} &nbsp;&nbsp;
                                {/*
                                  Matches VB exactly: lblHasLP.Text is only ever explicitly set to
                                  "Y" when a lesson plan exists; otherwise it keeps its ASPX
                                  design-time placeholder text "aaa" (never reset to "N"). That's a
                                  quirk in the legacy page, reproduced here on purpose.
                                */}
                                <span style={{ color: '#000000', fontWeight: 'normal' }}>Has LessonPlan: </span> {details?.hasLP === true || details?.hasLP === 'Y' || details?.hasLP === 'y' ? 'Y' : 'aaa'}
                            </td>
                            <td style={{ textAlign: 'right', marginTop: '10px' }}>
                                <button
                                    onClick={handleCreate}
                                    disabled={loading || !details}
                                    style={{ backgroundColor: 'orange', border: '2px solid #119ECC', color: 'white', fontWeight: 'bold', width: '90px', height: '28px', marginRight: '10px', cursor: 'pointer' }}
                                >
                                    Create
                                </button>
                                <button
                                    onClick={handleView}
                                    disabled={loading || !details}
                                    style={{ backgroundColor: 'orange', border: '2px solid #119ECC', color: 'white', fontWeight: 'bold', width: '90px', height: '28px', cursor: 'pointer' }}
                                >
                                    View
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {lessonPlan.length > 0 && (
                <div
                    style={{
                        marginTop: '5px',
                        overflow: 'auto',
                        border: '1px solid #80a0ff',
                        maxHeight: '750px',
                        width: '100%',
                        textAlign: 'left'
                    }}
                >
                    <table
                        style={{
                            borderCollapse: 'collapse',
                            fontSize: '10pt',
                            whiteSpace: 'nowrap',
                            display: 'inline-table'
                        }}
                    >
                        <thead style={{ backgroundColor: '#5792CB', color: 'white', fontWeight: 'bold', position: 'sticky', top: 0 }}>
                            <tr>
                                <th style={{ borderRight: '1px solid #F4FAFF', padding: '6px', whiteSpace: 'nowrap' }}>Lesson Position</th>
                                <th style={{ borderRight: '1px solid #F4FAFF', padding: '6px', whiteSpace: 'nowrap' }}>DicName</th>
                                <th style={{ borderRight: '1px solid #F4FAFF', padding: '6px', whiteSpace: 'nowrap' }}>Lesson Name</th>
                                <th style={{ borderRight: '1px solid #F4FAFF', padding: '6px', whiteSpace: 'nowrap' }}>Activity Type</th>
                                <th style={{ borderRight: '1px solid #F4FAFF', padding: '6px', whiteSpace: 'nowrap' }}>Strand</th>
                                <th style={{ borderRight: '1px solid #F4FAFF', padding: '6px', whiteSpace: 'nowrap' }}>Sub Strand</th>
                                <th style={{ borderRight: '1px solid #F4FAFF', padding: '6px', whiteSpace: 'nowrap' }}>Year</th>
                                <th style={{ borderRight: '1px solid #F4FAFF', padding: '6px', whiteSpace: 'nowrap' }}>How Added</th>
                                <th style={{ borderRight: '1px solid #F4FAFF', padding: '6px', whiteSpace: 'nowrap' }}>Lesson Time</th>
                                <th style={{ borderRight: '1px solid #F4FAFF', padding: '6px', whiteSpace: 'nowrap' }}>Week No</th>
                                <th style={{ borderRight: '1px solid #F4FAFF', padding: '6px', whiteSpace: 'nowrap' }}>Plan CreatedOn</th>
                                <th style={{ borderRight: '1px solid #F4FAFF', padding: '6px', whiteSpace: 'nowrap', width: '100%' }}>WeekendDate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lessonPlan.map((row, idx) => (
                                <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#F4FAFF' : 'white', fontSize: '10pt' }}>
                                    <td style={{ borderRight: '1px solid #5792CB', padding: '6px', whiteSpace: 'nowrap', textAlign: 'center' }}>{row.LessonPosition}</td>
                                    <td style={{ borderRight: '1px solid #5792CB', padding: '6px', whiteSpace: 'nowrap' }}>{row.DicName}</td>
                                    <td style={{ borderRight: '1px solid #5792CB', padding: '6px', whiteSpace: 'nowrap' }}>{row.LessonName}</td>
                                    <td style={{ borderRight: '1px solid #5792CB', padding: '6px', whiteSpace: 'nowrap' }}>{row.ActivityType}</td>
                                    <td style={{ borderRight: '1px solid #5792CB', padding: '6px', whiteSpace: 'nowrap' }}>{row.Strand}</td>
                                    <td style={{ borderRight: '1px solid #5792CB', padding: '6px', whiteSpace: 'nowrap' }}>{row.SubStrand}</td>
                                    <td style={{ borderRight: '1px solid #5792CB', padding: '6px', whiteSpace: 'nowrap', textAlign: 'center' }}>{row.Year}</td>
                                    <td style={{ borderRight: '1px solid #5792CB', padding: '6px', whiteSpace: 'nowrap' }}>{row.HowAdded}</td>
                                    <td style={{ borderRight: '1px solid #5792CB', padding: '6px', whiteSpace: 'nowrap', textAlign: 'center' }}>{row.LessonTime}</td>
                                    <td style={{ borderRight: '1px solid #5792CB', padding: '6px', whiteSpace: 'nowrap', textAlign: 'center' }}>{row.WeekNumber}</td>
                                    <td style={{ borderRight: '1px solid #5792CB', padding: '6px', whiteSpace: 'nowrap' }}>
                                        {row.PlanCreatedOn ? new Date(row.PlanCreatedOn).toLocaleDateString('en-GB') : ''}
                                    </td>
                                    <td style={{ borderRight: '1px solid #5792CB', padding: '5px', whiteSpace: 'nowrap' }}>
                                        {row.WeekendDate ? new Date(row.WeekendDate).toLocaleDateString('en-GB') : ''}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default CreateMathsDoctorLessonPlan;